import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  RequestStatus,
  StaffVideoConsultRequest,
  StaffVideoConsultRequestList,
  StaffVideoSession,
  StaffVideoSessionList,
  TeleconsultType,
  VideoCallToken,
  VideoFee,
  VideoFeeInput,
  VideoOutcomeInput,
  VideoRecording,
  VideoRecordingAccess,
  VideoRecordingList,
} from './types';
import { LIVE_STATUSES } from './types';

/** The API asks clients to poll every few seconds while a session is live. */
export const LIVE_POLL_MS = 4000;
const LIST_POLL_MS = 15000;

const SESSIONS = 'video-sessions';
const REQUESTS = 'video-consult-requests';

/** GET /video-sessions: the doctor's video queue for a clinic day. */
export function useVideoSessions(date: string, mine: boolean) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: [SESSIONS, selected?.id ?? 'all', date, mine],
    queryFn: ({ signal }) =>
      api
        .get<StaffVideoSessionList>('/video-sessions', {
          signal,
          query: { date, ...(mine ? { mine: 'true' } : {}) },
        })
        .then((r) => r.data),
    refetchInterval: LIST_POLL_MS,
  });
}

/** GET /video-sessions/{id}, polled while the session is live. */
export function useVideoSession(id: string) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: [SESSIONS, selected?.id ?? 'all', 'one', id],
    queryFn: ({ signal }) =>
      api
        .get<StaffVideoSession>(`/video-sessions/${id}`, { signal })
        .then((r) => r.data),
    refetchInterval: (q) =>
      q.state.data && LIVE_STATUSES.includes(q.state.data.status)
        ? LIVE_POLL_MS
        : false,
  });
}

/** GET /video-consults/requests: requests patients started in the app. */
export function useVideoConsultRequests(status: RequestStatus = 'requested') {
  const { selected } = useBranch();
  return useQuery({
    queryKey: [REQUESTS, selected?.id ?? 'all', status],
    queryFn: ({ signal }) =>
      api
        .get<StaffVideoConsultRequestList>('/video-consults/requests', {
          signal,
          query: { status },
        })
        .then((r) => r.data),
    refetchInterval: status === 'requested' ? LIST_POLL_MS : false,
  });
}

/** Any video action: refreshes sessions, requests and the teleconsult queue. */
export function useVideoAction<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [SESSIONS] });
      void qc.invalidateQueries({ queryKey: [REQUESTS] });
      void qc.invalidateQueries({ queryKey: ['teleconsults'] });
    },
  });
}

const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data);

export const videoApi = {
  /** POST /video-sessions/{id}/token (no-store; at most 1 hour). */
  token: (id: string) =>
    data(api.post<VideoCallToken>(`/video-sessions/${id}/token`)),
  admit: ({ id, version }: { id: string; version: number }) =>
    data(
      api.post<StaffVideoSession>(`/video-sessions/${id}/admit`, { version }),
    ),
  /** POST /video-sessions/{id}/pay-later: join before paying (fee due -> pay_later). */
  payLater: ({ id, version }: { id: string; version: number }) =>
    data(
      api.post<StaffVideoSession>(`/video-sessions/${id}/pay-later`, {
        version,
      }),
    ),
  identityCheck: ({ id, version }: { id: string; version: number }) =>
    data(
      api.post<StaffVideoSession>(`/video-sessions/${id}/identity-check`, {
        version,
        identityConfirmed: true,
      }),
    ),
  end: ({
    id,
    version,
    outcome,
  }: {
    id: string;
    version: number;
    outcome?: VideoOutcomeInput;
  }) =>
    data(
      api.post<StaffVideoSession>(`/video-sessions/${id}/end`, {
        version,
        ...(outcome ? { outcome } : {}),
      }),
    ),
  outcome: ({
    id,
    version,
    outcome,
  }: {
    id: string;
    version: number;
    outcome: VideoOutcomeInput;
  }) =>
    data(
      api.put<StaffVideoSession>(`/video-sessions/${id}/outcome`, {
        version,
        outcome,
      }),
    ),
  accept: ({
    id,
    ...body
  }: {
    id: string;
    version: number;
    startsAt: string;
    type: TeleconsultType;
    payLater: boolean;
  }) =>
    data(
      api.post<StaffVideoConsultRequest>(
        `/video-consults/requests/${id}/accept`,
        body,
      ),
    ),
  decline: ({ id, ...body }: { id: string; version: number; reason: string }) =>
    data(
      api.post<StaffVideoConsultRequest>(
        `/video-consults/requests/${id}/decline`,
        body,
      ),
    ),
};

const FEE = ['video-consult-fee'];

/** GET /video-consults/fee (services:read or teleconsults:read). */
export function useVideoFee() {
  return useQuery({
    queryKey: FEE,
    queryFn: ({ signal }) =>
      api.get<VideoFee>('/video-consults/fee', { signal }).then((r) => r.data),
  });
}

/** PUT /video-consults/fee (services:manage): one fee for every branch. */
export function useSaveVideoFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VideoFeeInput) =>
      api.put<VideoFee>('/video-consults/fee', body).then((r) => r.data),
    onSuccess: (saved) => {
      qc.setQueryData(FEE, saved);
      // The API gives the "Video consultation" service the same price.
      void qc.invalidateQueries({ queryKey: ['fee-services'] });
    },
  });
}

const RECORDINGS = 'video-recordings';

/**
 * GET /video-sessions/{id}/recordings (RL-2158). A 404 RECORDING_DISABLED means
 * recording is switched off on this server: the panel hides itself.
 */
export function useVideoRecordings(sessionId: string, enabled: boolean) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: [RECORDINGS, selected?.id ?? 'all', sessionId],
    enabled,
    retry: false,
    queryFn: ({ signal }) =>
      api
        .get<VideoRecordingList>(`/video-sessions/${sessionId}/recordings`, {
          signal,
        })
        .then((r) => r.data),
    refetchInterval: (q) =>
      q.state.data?.recordings.some((r) =>
        ['requested', 'recording', 'processing', 'ingesting'].includes(
          r.status,
        ),
      )
        ? LIVE_POLL_MS
        : false,
  });
}

export const recordingApi = {
  start: (id: string) =>
    data(
      api.post<VideoRecording>(`/video-sessions/${id}/recordings/start`, {
        consentConfirmed: true,
      }),
    ),
  stop: (id: string) =>
    data(api.post<VideoRecording>(`/video-sessions/${id}/recordings/stop`, {})),
  access: (recordingId: string) =>
    data(
      api.post<VideoRecordingAccess>(
        `/video-recordings/${recordingId}/access`,
        {},
      ),
    ),
};

/** Start or stop a recording, then refresh the list. */
export function useRecordingAction<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [RECORDINGS] });
    },
  });
}
